import React from 'react'
import {Container} from '@components/ui/container/Container.tsx'
import {useTranslation} from 'react-i18next'
import {ArrowLeftIcon, Key01Icon} from '@components/ui/icon/Icon.tsx'
import {IconContainer} from '@components/ui/icon-container/IconContainer.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {InputText} from '@components/commons/input-text/InputText.tsx'
import {SubmitHandler, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {
    FORGOT_PASSWORD_FORM_MODEL,
    ForgotPasswordFormSchema,
    ForgotPasswordValidationSchema
} from '@/features/auth/components/forgot-password-form/ForgotPasswordFormModel.ts'
import {Button} from '@components/ui/button/Button.tsx'
import {ButtonLink} from '@components/ui/button-link/ButtonLink.tsx'
import {routes} from '@utilities/constants/routes.tsx'
import {useForgotPassword} from '@/features/auth/services/query/useForgotPassword.ts'
import {StyledAuthBoxWrapper, StyledAuthWrapper} from '@pages/login/style.ts'
import {useNavigate} from 'react-router-dom'

export const ForgotPassword: React.FC = () => {
    const {t} = useTranslation()
    const {
        register,
        handleSubmit,
        formState: {errors, isSubmitting, isValid}
    } = useForm<ForgotPasswordValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(ForgotPasswordFormSchema)
    })
    const navigate = useNavigate()
    const {mutate: mutateForgotPassword, isPending: isPEndingForgotPassword} = useForgotPassword({
        onSuccess: () => navigate(routes.EMAIL_SENT.path)
    })
    const onSubmit: SubmitHandler<ForgotPasswordValidationSchema> = data => {
        mutateForgotPassword(data)
    }
    return (
        <Container>
            <StyledAuthWrapper>
                <StyledAuthBoxWrapper>
                    <Flexbox justify={'center'}>
                        <IconContainer size={'lg'}>
                            <Key01Icon />
                        </IconContainer>
                    </Flexbox>
                    <Flexbox direction={'column'} gap={3}>
                        <h4>{t('forgotPassword:title')}</h4>
                        <p>{t('forgotPassword:subtitle')}</p>
                    </Flexbox>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Flexbox direction={'column'} gap={6} align={'stretch'}>
                            <InputText
                                label={t(FORGOT_PASSWORD_FORM_MODEL.email.label)}
                                type={'email'}
                                placeholder={t(FORGOT_PASSWORD_FORM_MODEL.email.placeholder)}
                                errorMessage={t(errors.email?.message || '')}
                                {...register(t(FORGOT_PASSWORD_FORM_MODEL.email.name))}
                            />
                            <Button
                                type={'submit'}
                                fullWidth
                                variant={'primary'}
                                disabled={isSubmitting || isPEndingForgotPassword || !isValid}
                            >
                                {t('forgotPassword:primary_cta')}
                            </Button>
                        </Flexbox>
                    </form>
                    <Flexbox justify={'center'}>
                        <ButtonLink to={routes.LOGIN.path} variant={'ghost'}>
                            <ArrowLeftIcon />
                            {t('forgotPassword:secondary_cta')}
                        </ButtonLink>
                    </Flexbox>
                </StyledAuthBoxWrapper>
            </StyledAuthWrapper>
        </Container>
    )
}

ForgotPassword.displayName = 'ForgotPassword'
